import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar, Activity, LogOut, Menu, X, DollarSign, User, Scale } from 'lucide-react';
import { ThemeToggle } from '../components/ThemeToggle';
import { PatientAppointments } from '../components/patient/PatientAppointments';
import { PatientEvolution } from '../components/patient/PatientEvolution';
import { PatientPayments } from '../components/patient/PatientPayments';
import { PatientProfile } from '../components/patient/PatientProfile';
import { NextActionHero } from '../components/patient/NextActionHero';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export const PatientDashboard = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [patientData, setPatientData] = useState(null);
  const [appointments, setAppointments] = useState([]);

  const fetchPatientData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/patient/my-info`, { withCredentials: true });
      setPatientData(data);
    } catch (error) {
      console.error('Error fetching patient data:', error);
    }
  }, []);

  const fetchAppointments = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/patient/my-appointments`, { withCredentials: true });
      setAppointments(data || []);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    }
  }, []);

  useEffect(() => {
    fetchPatientData();
    fetchAppointments();
  }, [fetchPatientData, fetchAppointments]);

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  const navItems = [
    { path: '/patient', label: 'Dashboard', icon: Activity },
    { path: '/patient/appointments', label: 'Consultas', icon: Calendar },
    { path: '/patient/evolution', label: 'Peso e Medidas', icon: Scale },
    { path: '/patient/payments', label: 'Pagamentos', icon: DollarSign },
    { path: '/patient/profile', label: 'Meu Cadastro', icon: User },
  ];

  return (
    <div className="min-h-screen bg-[#F5F0E8] dark:bg-[#0A1230]">
      {/* Header */}
      <header className="bg-white dark:bg-[#0F1B3D] border-b border-[#E5DCC8] dark:border-[#1A2952] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <button
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
              <img
                src="/images/logo-gp.png"
                alt="Garcia Passos Clinica Medica"
                className="h-10 sm:h-12"
              />
            </div>
            <div className="flex items-center gap-3">
              <span className="hidden sm:inline text-sm text-[#5F6B85] dark:text-[#FAF6EE]/70">{user?.name}</span>
              <ThemeToggle />
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="border-[#E5DCC8] dark:border-[#1A2952] dark:text-[#FAF6EE]"
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sair
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Sidebar */}
          <aside className={`md:w-64 ${mobileMenuOpen ? 'block' : 'hidden md:block'}`}>
            <nav className="space-y-2" data-testid="patient-navigation">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                      isActive
                        ? 'bg-[#0F1B3D] text-white'
                        : 'text-[#5F6B85] hover:bg-[#FAF6EE]'
                    }`}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            <Routes>
              <Route path="/" element={<PatientHome appointments={appointments} patientData={patientData} onRefresh={fetchAppointments} />} />
              <Route path="/appointments" element={<PatientAppointments />} />
              <Route path="/evolution" element={<PatientEvolution />} />
              <Route path="/payments" element={<PatientPayments />} />
              <Route path="/profile" element={<PatientProfile />} />
            </Routes>
          </main>
        </div>
      </div>
    </div>
  );
};

const PatientHome = ({ appointments, patientData, onRefresh }) => {
  const [evolution, setEvolution] = useState([]);
  const [cronograma, setCronograma] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/evolution`, { withCredentials: true });
        setEvolution((data || []).filter(e => e.source === 'doctor' && (e.queixa_principal || e.conduta_final)));
      } catch (e) { /* noop */ }
      try {
        const { data } = await axios.get(`${API_URL}/api/patient/my-cronograma`, { withCredentials: true });
        if (data && (data.cronograma_uso?.some(r => r.semanas || r.dose) || data.cronograma_desmame?.some(r => r.semanas || r.dose))) {
          setCronograma(data);
        }
      } catch (e) { /* noop */ }
    };
    fetch();
  }, []);

  return (
    <div className="space-y-6" data-testid="patient-dashboard">
      <div>
        <h2 className="text-3xl font-medium text-[#0F1B3D] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Olá, {patientData?.name?.split(' ')[0] || 'Paciente'}
        </h2>
        <p className="text-[#5F6B85]">Veja o status da sua próxima consulta e acompanhe sua evolução</p>
      </div>

      {/* HERO: próxima ação */}
      <NextActionHero patientData={patientData} appointments={appointments} onRefresh={onRefresh} />

      {/* Cronograma Tirzepatida (uso + desmame) */}
      {cronograma && (
        <Card className="border-[#E5DCC8]">
          <CardHeader>
            <CardTitle className="text-base">Seu plano de tratamento</CardTitle>
            <CardDescription>Cronograma definido pela Dra. Fernanda</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {(cronograma.tempo_total || cronograma.perda_total_esperada || cronograma.meta_combinada) && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {cronograma.tempo_total && (
                  <div className="rounded-lg border border-[#E5DCC8] bg-[#FAF6EE] p-3">
                    <p className="text-[10px] uppercase tracking-wider text-[#7A85A0]">Duração</p>
                    <p className="text-lg font-semibold text-[#0F1B3D]">{cronograma.tempo_total}</p>
                  </div>
                )}
                {cronograma.perda_total_esperada && (
                  <div className="rounded-lg border border-[#E5DCC8] bg-[#FAF6EE] p-3">
                    <p className="text-[10px] uppercase tracking-wider text-[#7A85A0]">Perda esperada</p>
                    <p className="text-lg font-semibold text-[#0F1B3D]">{cronograma.perda_total_esperada} kg</p>
                  </div>
                )}
                {cronograma.meta_combinada && (
                  <div className="rounded-lg border border-[#E5DCC8] bg-[#FAF6EE] p-3">
                    <p className="text-[10px] uppercase tracking-wider text-[#7A85A0]">Meta combinada</p>
                    <p className="text-lg font-semibold text-[#0F1B3D]">{cronograma.meta_combinada} kg</p>
                  </div>
                )}
              </div>
            )}

            {cronograma.cronograma_uso?.some(r => r.semanas || r.dose) && (
              <div>
                <h4 className="text-sm font-semibold text-[#0F1B3D] mb-2">Cronograma de Uso</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-[#E5DCC8] rounded-lg">
                    <thead className="bg-[#FAF6EE] text-xs text-[#5F6B85]">
                      <tr>
                        <th className="p-2 text-left">Fase</th>
                        <th className="p-2">Semanas</th>
                        <th className="p-2">Dose</th>
                        <th className="p-2">Perda esperada</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cronograma.cronograma_uso.filter(r => r.semanas || r.dose).map((r, i) => (
                        <tr key={`u-${i}`} className="border-t border-[#E5DCC8]">
                          <td className="p-2 font-medium text-[#0F1B3D]">{r.fase}</td>
                          <td className="p-2 text-center text-[#5F6B85]">{r.semanas || '—'}</td>
                          <td className="p-2 text-center text-[#5F6B85]">{r.dose || '—'}</td>
                          <td className="p-2 text-center text-[#5F6B85]">{r.perda_total || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {cronograma.cronograma_desmame?.some(r => r.semanas || r.dose) && (
              <div>
                <h4 className="text-sm font-semibold text-[#0F1B3D] mb-2">Cronograma de Desmame</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-[#E5DCC8] rounded-lg">
                    <thead className="bg-[#FAF6EE] text-xs text-[#5F6B85]">
                      <tr>
                        <th className="p-2 text-left">Fase</th>
                        <th className="p-2">Semanas</th>
                        <th className="p-2">Dose</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cronograma.cronograma_desmame.filter(r => r.semanas || r.dose).map((r, i) => (
                        <tr key={`d-${i}`} className="border-t border-[#E5DCC8]">
                          <td className="p-2 font-medium text-[#0F1B3D]">{r.fase}</td>
                          <td className="p-2 text-center text-[#5F6B85]">{r.semanas || '—'}</td>
                          <td className="p-2 text-center text-[#5F6B85]">{r.dose || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Timeline de evolução do médico */}
      {evolution.length > 0 && (
        <Card className="border-[#E5DCC8]">
          <CardHeader>
            <CardTitle className="text-base">Histórico de Consultas</CardTitle>
            <CardDescription>Condutas registradas pela doutora</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative pl-6 space-y-4">
              <div className="absolute left-2 top-0 bottom-0 w-px bg-[#E5DCC8]" />
              {evolution.slice(0, 5).map((e, i) => (
                <div key={e.record_id || i} className="relative">
                  <div className="absolute -left-[18px] top-1 w-3 h-3 rounded-full bg-[#0F1B3D] border-2 border-white" />
                  <div className="bg-[#FAF6EE] rounded-lg p-3 border border-[#E5DCC8]">
                    <p className="text-xs text-[#7A85A0] mb-1">{new Date(e.created_at).toLocaleDateString('pt-BR')}</p>
                    {e.conduta_final && <p className="text-sm text-[#5F6B85]"><strong className="text-[#0F1B3D]">Conduta:</strong> {e.conduta_final}</p>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};